import { AlertTriangle, CircleDollarSign, GitBranch } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { conversationPath } from '@/config/routes';
import { formatClockTime, formatDateTime } from '@/utils/datetime';
import { LOADING_VALUE } from '@/utils/placeholders';

import { PauseControl } from './Dashboard.PauseControl';
import { styles } from './Dashboard.styles';
import type { CellErrorProps, StatusStripProps } from './Dashboard.types';
import {
	alertsDotClass,
	creditsDotClass,
	hasCreditExhaustion,
	pauseDotClass,
	trafficSharePct,
} from './Dashboard.utils';

const CellError = ({ labelKey }: CellErrorProps) => {
	const { t } = useTranslation('translation', { keyPrefix: 'dashboard' });

	return (
		<div className={styles.statusCell}>
			<p className={styles.statusCellLabel}>{t(labelKey)}</p>
			<p className="text-[13px] text-error">{t('CELL_ERROR')}</p>
		</div>
	);
};

export const ValentinaCell = ({ pause, pauseControl }: Pick<StatusStripProps, 'pause' | 'pauseControl'>) => {
	const { t } = useTranslation('translation', { keyPrefix: 'dashboard' });

	if (pause.isError) return <CellError labelKey="VALENTINA_LABEL" />;

	const state = pause.data;

	return (
		<div className={styles.statusCell}>
			<p className={styles.statusCellLabel}>{t('VALENTINA_LABEL')}</p>
			<p className={styles.statusCellBig}>
				<span className={pauseDotClass(state)} />
				{!state && LOADING_VALUE}
				{state && (state.paused ? t('VALENTINA_PAUSED') : t('VALENTINA_RESPONDING'))}
			</p>
			{state?.changedBy && (
				<p className={styles.statusCellSub}>
					{t('VALENTINA_CHANGED_BY', { who: state.changedBy, when: formatDateTime(state.changedAt) })}
				</p>
			)}
			{state?.reason && <p className={styles.statusCellSub}>{t('VALENTINA_REASON', { reason: state.reason })}</p>}
			<PauseControl {...pauseControl} />
		</div>
	);
};

export const CreditsCell = ({ dependencies }: Pick<StatusStripProps, 'dependencies'>) => {
	const { t } = useTranslation('translation', { keyPrefix: 'dashboard' });

	if (dependencies.isError) return <CellError labelKey="CREDITS_LABEL" />;

	const { data } = dependencies;
	const exhausted = hasCreditExhaustion(data);

	return (
		<div className={styles.statusCell}>
			<p className={styles.statusCellLabel}>
				<CircleDollarSign className="h-sm w-sm" aria-hidden="true" />
				{t('CREDITS_LABEL')}
			</p>
			<p className={styles.statusCellBig}>
				<span className={creditsDotClass(data)} />
				{!data && LOADING_VALUE}
				{data && (exhausted ? t('CREDITS_EXHAUSTED') : t('CREDITS_OK'))}
			</p>
			{data && (
				<p className={styles.statusCellSub}>{t('CREDITS_CHECKED_AT', { time: formatClockTime(data.checkedAt) })}</p>
			)}
		</div>
	);
};

export const AlertsCell = ({ alerts }: Pick<StatusStripProps, 'alerts'>) => {
	const { t } = useTranslation('translation', { keyPrefix: 'dashboard' });

	if (alerts.isError) return <CellError labelKey="ALERTS_LABEL" />;

	const items = alerts.data?.items;
	const newest = items?.[0];

	return (
		<div className={styles.statusCell}>
			<p className={styles.statusCellLabel}>
				<AlertTriangle className="h-sm w-sm" aria-hidden="true" />
				{t('ALERTS_LABEL')}
			</p>
			<p className={styles.statusCellBig}>
				<span className={alertsDotClass(items?.length ?? null)} />
				{!items && LOADING_VALUE}
				{items?.length}
			</p>
			{items?.length === 0 && <p className={styles.statusCellSub}>{t('ALERTS_EMPTY')}</p>}
			{newest && !newest.conversationId && <p className={styles.statusCellSub}>{newest.title}</p>}
			{newest?.conversationId && (
				<Link
					to={conversationPath(newest.conversationId)}
					className={`${styles.statusCellSub} transition-colors hover:text-purple-700`}
				>
					{newest.title}
				</Link>
			)}
		</div>
	);
};

export const VersionCell = ({ routing }: Pick<StatusStripProps, 'routing'>) => {
	const { t } = useTranslation('translation', { keyPrefix: 'dashboard' });

	if (routing.isError) return <CellError labelKey="VERSION_LABEL" />;

	const { data } = routing;

	return (
		<div className={styles.statusCell}>
			<p className={styles.statusCellLabel}>
				<GitBranch className="h-sm w-sm" aria-hidden="true" />
				{t('VERSION_LABEL')}
			</p>
			<p className={styles.statusCellBig}>
				{!data && LOADING_VALUE}
				{data && t('VERSION_TRAFFIC', { version: data.default, pct: trafficSharePct(data) })}
			</p>
			{data && <p className={styles.statusCellSub}>{t('VERSION_HINT')}</p>}
		</div>
	);
};
